import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Loader2,
  RotateCw,
  ChevronDown,
  ChevronUp,
  Webhook,
  Inbox,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminSubscriptionApi } from './adminApi';

const PROCESS_STATUS_CONFIG = {
  processed: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2, dotColor: 'bg-green-500' },
  pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, dotColor: 'bg-yellow-500' },
  failed: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, dotColor: 'bg-red-500' },
  dead_letter: { color: 'bg-gray-100 text-gray-500 border-gray-200', icon: Inbox, dotColor: 'bg-gray-400' },
};

const ProcessBadge = ({ status }) => {
  const config = PROCESS_STATUS_CONFIG[status] || PROCESS_STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`} />
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const PROVIDERS = ['stripe', 'paypal'];
const EVENT_TYPES = [
  'invoice.payment_succeeded', 'invoice.payment_failed',
  'customer.subscription.created', 'customer.subscription.updated',
  'customer.subscription.deleted', 'charge.refunded',
  'charge.dispute.created',
];
const PROCESS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'processed', label: 'Processed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'dead_letter', label: 'Dead Letter' },
];

export default function AdminWebhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [filters, setFilters] = useState({ provider: '', event_type: '', processed: '', date_from: '', date_to: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  // Payload modal
  const [payloadModal, setPayloadModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookDetailLoading, setWebhookDetailLoading] = useState(false);

  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

      const res = await adminSubscriptionApi.listWebhooks(params);
      setWebhooks(res.data || res.webhooks || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error('Failed to load webhook events');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);
  useEffect(() => { setPage(1); }, [filters]);

  const totalPages = Math.ceil(total / perPage) || 1;

  const handleRetry = async (id, e) => {
    e.stopPropagation();
    setRetryingId(id);
    try {
      await adminSubscriptionApi.retryWebhook(id);
      toast.success('Webhook retry queued');
      fetchWebhooks();
    } catch (err) {
      toast.error('Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const openPayload = async (wh) => {
    setWebhookDetailLoading(true);
    setPayloadModal(true);
    try {
      const full = await adminSubscriptionApi.getWebhook(wh.id);
      setSelectedWebhook(full);
    } catch {
      setSelectedWebhook(wh);
    } finally {
      setWebhookDetailLoading(false);
    }
  };

  const toggleRowExpand = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const clearFilters = () => {
    setFilters({ provider: '', event_type: '', processed: '', date_from: '', date_to: '' });
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  // Dead letter count
  const deadLetterCount = webhooks.filter((w) => w.status === 'dead_letter' || w.processed === 'dead_letter').length;

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Webhook Events</h1>
          <p className="text-sm text-muted-foreground">{total.toLocaleString()} event{total !== 1 ? 's' : ''}</p>
        </div>
        {deadLetterCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
            <Inbox className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">{deadLetterCount} dead letter{deadLetterCount !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${showFilters ? 'bg-primary text-primary-foreground' : 'border-input bg-background hover:bg-accent'}`}
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-4 w-4" /> Clear
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={fetchWebhooks}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              <RotateCw className="h-4 w-4" />
              Refresh
            </button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Provider</label>
                <select
                  value={filters.provider}
                  onChange={(e) => setFilters((f) => ({ ...f, provider: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="">All</option>
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Event Type</label>
                <select
                  value={filters.event_type}
                  onChange={(e) => setFilters((f) => ({ ...f, event_type: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  <option value="">All</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Status</label>
                <select
                  value={filters.processed}
                  onChange={(e) => setFilters((f) => ({ ...f, processed: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                >
                  {PROCESS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Date From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Date To</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="h-8 px-2 text-sm rounded-md border border-input bg-background"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Timestamp</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Event Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Error</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-muted rounded animate-pulse w-16" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : webhooks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No webhook events found</TableCell>
                </TableRow>
              ) : (
                webhooks.map((wh) => {
                  const status = wh.processed || wh.status || 'pending';
                  const isExpanded = expandedRow === wh.id;
                  return (
                    <React.Fragment key={wh.id}>
                      <TableRow className="cursor-pointer" onClick={() => toggleRowExpand(wh.id)}>
                        <TableCell>
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground text-xs">{formatDate(wh.created_at || wh.timestamp)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{wh.provider}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{wh.event_type}</TableCell>
                        <TableCell><ProcessBadge status={status} /></TableCell>
                        <TableCell className="text-center">{wh.attempts ?? '-'}</TableCell>
                        <TableCell className="text-xs text-red-600 max-w-[200px] truncate">{wh.error || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => openPayload(wh)}
                              className="p-1.5 rounded-md hover:bg-accent transition-colors"
                              title="View payload"
                            >
                              <Webhook className="h-4 w-4" />
                            </button>
                            {(status === 'failed' || status === 'dead_letter') && (
                              <button
                                onClick={(e) => handleRetry(wh.id, e)}
                                disabled={retryingId === wh.id}
                                className="p-1.5 rounded-md hover:bg-accent transition-colors disabled:opacity-50"
                                title="Retry"
                              >
                                {retryingId === wh.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-muted/30 p-0">
                            <div className="p-4">
                              <p className="text-xs font-medium text-muted-foreground mb-2">Raw Payload</p>
                              <pre className="p-3 bg-background border rounded-md text-xs overflow-auto max-h-64 font-mono leading-relaxed">
                                {wh.payload ? (
                                  typeof wh.payload === 'string'
                                    ? (() => { try { return JSON.stringify(JSON.parse(wh.payload), null, 2); } catch { return wh.payload; } })()
                                    : JSON.stringify(wh.payload, null, 2)
                                ) : 'No payload available - click the webhook icon to load full details'}
                              </pre>
                              {wh.error && (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-red-600 mb-1">Error Details</p>
                                  <pre className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-800 overflow-auto max-h-32">{wh.error}</pre>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {Math.min(((page - 1) * perPage) + 1, total)}-{Math.min(page * perPage, total)} of {total}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-md border border-input hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-3 py-1 text-sm font-medium">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-md border border-input hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Dead Letter Queue Section */}
      <Card className="border-gray-300">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Inbox className="h-4 w-4 text-gray-500" />
            Dead Letter Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Events that have exhausted all retry attempts. These may require manual investigation.
          </p>
          <button
            onClick={() => setFilters((f) => ({ ...f, processed: 'dead_letter' }))}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-input bg-background hover:bg-accent transition-colors"
          >
            <AlertCircle className="h-4 w-4" />
            View Dead Letter Events
          </button>
        </CardContent>
      </Card>

      {/* Payload Detail Modal */}
      <Dialog open={payloadModal} onOpenChange={setPayloadModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="h-5 w-5" />
              Webhook Event Detail
            </DialogTitle>
          </DialogHeader>
          {webhookDetailLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : selectedWebhook && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-muted-foreground">ID</p><p className="font-mono text-xs">{selectedWebhook.id}</p></div>
                <div><p className="text-muted-foreground">Provider</p><p>{selectedWebhook.provider}</p></div>
                <div><p className="text-muted-foreground">Event Type</p><p className="font-mono text-xs">{selectedWebhook.event_type}</p></div>
                <div><p className="text-muted-foreground">Status</p><p><ProcessBadge status={selectedWebhook.processed || selectedWebhook.status} /></p></div>
                <div><p className="text-muted-foreground">Attempts</p><p>{selectedWebhook.attempts ?? '-'}</p></div>
                <div><p className="text-muted-foreground">Received</p><p>{formatDate(selectedWebhook.created_at || selectedWebhook.timestamp)}</p></div>
              </div>
              {selectedWebhook.error && (
                <div>
                  <p className="text-sm font-medium text-red-600 mb-1">Error</p>
                  <pre className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-800 overflow-auto max-h-32">{selectedWebhook.error}</pre>
                </div>
              )}
              <div>
                <p className="text-sm font-medium mb-2">Payload</p>
                <pre className="p-4 bg-muted rounded-md text-xs overflow-auto max-h-96 font-mono leading-relaxed">
                  {selectedWebhook.payload ? (
                    typeof selectedWebhook.payload === 'string'
                      ? (() => { try { return JSON.stringify(JSON.parse(selectedWebhook.payload), null, 2); } catch { return selectedWebhook.payload; } })()
                      : JSON.stringify(selectedWebhook.payload, null, 2)
                  ) : 'No payload data'}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
